import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  FileSpreadsheet, Loader2, AlertTriangle, RefreshCw, Download, CheckCircle2, XCircle, Mail,
  ChevronRight, ChevronDown,
} from 'lucide-react';
import { escCsv } from '../../../shared/utils/csvSafe'; // CHA-193 — shared formula-injection-safe CSV escaper
import { ACE_HEADERS, aceCellsForStyle } from '../../../shared/utils/cpscBrokerCsv'; // CHA-233 — shared ACE row/header (cron + UI single source)
import CpscTable, { CPSC_TH_CLASS } from './CpscTable'; // CHA-230 — shared CPSC table shell

/**
 * CpscBrokerHandoff — CHA-204. Broker WORKLIST: one compact line per commercial invoice shipped to the US,
 * flagged COMPLETE (every CPSC-required style certified) or MISSING (open the row to see which styles + POs).
 * Data from getCpscBrokerInvoices (openwip csv, stage='SHIPPED', joined to the CHA-179 cert resolver).
 * Read-only. Shows division / customer / ship date / open units so old, mostly-received shipments stand out.
 */
const s = (v) => String(v ?? '').trim();
const fmtDate = (d) => (s(d) ? s(d).slice(0, 10) : '—');
const fmtUnits = (n) => (Number(n) || 0).toLocaleString();
const receiptNote = (i) => {
  const r = i?.receiptSummary;
  if (!r?.mixed) return null;
  return `Partially received: ${fmtUnits(r.receivedUnits)} received, ${fmtUnits(r.openUnits)} open (${r.remainingPctLabel || `${r.remainingPct}%`} left)`;
};
const noReceiptNote = (i) => {
  const r = i?.noReceiptSummary;
  if (!r) return null;
  return `No receipt found: ${fmtUnits(r.openUnits)} open in WIP; CPSC is complete.`;
};
const receiptUnknownNote = (i) => {
  const r = i?.receiptEvidenceUnknown;
  if (!r) return null;
  if (r.reason === 'receipt_lookup_failed') return 'Receipt evidence unknown: receipts table could not be read.';
  return `Receipt evidence unknown: ship date ${fmtDate(r.shipDate)} is before receipt data starts ${fmtDate(r.coverageStartDate)}.`;
};

const CSV_HEADERS = ['Commercial Invoice', 'Division', 'Customer', 'Ship Date', 'Due Date', 'Open Units', 'Received Units', 'Remaining %', 'Receipt Note', 'Status', 'Required', 'Certified', 'Exempt', 'Not Subject', 'Missing', 'Missing Styles', 'POs'];

export default function CpscBrokerHandoff({ userProfile } = {}) {
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner';
  const isAdmin = isPlatformRole || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ total: 0, complete: 0, missing: 0 });
  const [excludedNonUs, setExcludedNonUs] = useState([]); // CHA-259 — POD-denylisted invoices (visible, never silent)
  const [podGateDegraded, setPodGateDegraded] = useState(false); // CHA-259 — POD lookup failed → gate ran open
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('missing');
  const [expanded, setExpanded] = useState(() => new Set());
  const [rulingBusy, setRulingBusy] = useState(null); // CHA-281 — styleNo currently being ruled
  // CHA-521 — on-demand CPSC action email, grouped by division + customer.
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [actionGroup, setActionGroup] = useState('');

  // CHA-521 — the (division, customer) pairs that actually have missing styles. Derived from the invoices
  // already on screen, so the picker needs no extra callable.
  const actionGroups = useMemo(() => {
    const m = new Map();
    for (const inv of invoices) {
      if (!inv.missingCount) continue;
      const division = String(inv.division ?? '').trim();
      const customer = String(inv.customer ?? '').trim();
      const key = `${division}||${customer}`;
      const e = m.get(key) || { key, division, customer, styles: 0, units: 0 };
      e.styles += inv.missingCount || 0;
      e.units += inv.totalUnits || 0;
      m.set(key, e);
    }
    return [...m.values()].sort((a, b) => a.division.localeCompare(b.division) || a.customer.localeCompare(b.customer));
  }, [invoices]);

  // Drop a selection whose group has left the worklist, so the picker never shows a key that no longer
  // resolves (belt-and-braces with the explicit `all` flag below).
  useEffect(() => {
    if (actionGroup && !actionGroups.some((g) => g.key === actionGroup)) setActionGroup('');
  }, [actionGroups, actionGroup]);

  // Send one group, or every group when `group` is null. The server re-derives the data — the UI only
  // names WHICH group, never what is in it.
  // `all` is EXPLICIT, never inferred from a falsy group. Codex/CodeRabbit #396: a selection kept across a
  // reload can point at a group that no longer has missing styles; `find` then returns undefined and a
  // "send this one" click silently became "email every group".
  const sendActionEmail = useCallback(async (group, { all = false } = {}) => {
    if (!all && !group) {
      setActionMsg({ ok: false, text: 'That division / customer group is no longer in the worklist. Reselect it.' });
      return;
    }
    setActionBusy(true); setActionMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'sendCpscActionEmails', { timeout: 330000 }); // CHA-528 — server's 300s window + 30s transport headroom
      // tenantId is REQUIRED for platform roles — resolveTenant rejects a platform admin without one, so
      // both buttons would have failed with invalid-argument for platform_owner (Codex/CodeRabbit #396).
      const res = await fn({
        ...(all ? {} : { division: group.division, customer: group.customer }),
        ...(isPlatformRole ? { tenantId } : {}),
      });
      const d = res?.data || {};
      const sentCount = d.sent || 0;
      const failedCount = d.skipped || 0;
      // Codex #396 — NAME the groups that failed. A bare count leaves send-all as the only available retry,
      // which re-sends every already-delivered email and duplicates customer-visible messages. With the
      // identities shown the operator can pick that group and retry only it.
      const failed = (d.groups || []).filter((g) => g && g.sent === false);
      const failedNames = failed.map((g) => `Div ${g.division || '—'} · ${g.customer || '—'}`).join(', ');
      // A run where every group failed must NOT paint as success (§8.3 — never report a swallowed failure
      // as "0 synced" success).
      setActionMsg({
        ok: sentCount > 0 && failedCount === 0,
        text: `Sent ${sentCount} email${sentCount === 1 ? '' : 's'}${failedCount ? ` · ${failedCount} failed` : ''}.`
          + (failedNames ? ` Failed: ${failedNames} — select a group above to retry just that one.` : ''),
      });
    } catch (e) {
      setActionMsg({ ok: false, text: e?.message || 'Send failed.' });
    } finally {
      setActionBusy(false);
    }
  }, [isPlatformRole, tenantId]);

  const load = useCallback(async () => {
    // Reset the CHA-259 gate state alongside invoices — a FAILED refresh must not keep showing a stale
    // exclusion badge / degraded warning from the previous successful load (Codex P3 round 3).
    setLoading(true); setError(null); setInvoices([]); setExcludedNonUs([]); setPodGateDegraded(false);
    try {
      const fn = httpsCallable(getFunctions(), 'getCpscBrokerInvoices', { timeout: 150000 }); // CHA-382 / CHA-528 — server's 120s window + 30s transport headroom
      const res = await fn(isPlatformRole ? { tenantId } : {});
      setInvoices(res?.data?.invoices || []);
      setSummary(res?.data?.summary || { total: 0, complete: 0, missing: 0 });
      setExcludedNonUs(Array.isArray(res?.data?.excludedNonUs) ? res.data.excludedNonUs : []); // CHA-259
      setPodGateDegraded(res?.data?.podGateDegraded === true);
    } catch (e) {
      console.error('Failed to load broker invoices', e);
      setError(e?.message || 'Could not load shipped invoices. Check access and retry.');
    } finally {
      setLoading(false);
    }
  }, [isPlatformRole, tenantId]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  // CHA-281 — the operator's adult/children's ruling for a review-level style. The click is the ruling of
  // record: server-validated + audited; the worklist reloads so the invoice re-buckets immediately.
  const rule = useCallback(async (styleNo, classification, htsAtRuling) => {
    setRulingBusy(styleNo); setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'setCpscStyleClassification');
      await fn({ styleNo, classification, htsAtRuling, ...(isPlatformRole ? { tenantId } : {}) });
      await load();
    } catch (e) {
      console.error('setCpscStyleClassification failed', e);
      setError(e?.message || 'Could not save the ruling. Retry.');
    } finally {
      setRulingBusy(null);
    }
  }, [isPlatformRole, tenantId, load]);

  const visible = useMemo(() => invoices.filter((i) => (
    filter === 'all' ? true
      // "missing" = broker-worklist invoices with unresolved required styles (review-only ones live under "review")
      : filter === 'missing' ? (i.hasRequired && !i.complete)
        : filter === 'review' ? (i.reviewCount > 0) // CHA-281 — needs the adult/children's call
          : i.complete
  )), [invoices, filter]);

  const toggle = (ci) => setExpanded((p) => { const n = new Set(p); n.has(ci) ? n.delete(ci) : n.add(ci); return n; });

  const exportCsv = () => {
    if (!visible.length) { setError('No invoices to export.'); return; }
    const rows = visible.map((i) => [
      i.commInv, i.division || '', i.customer || '', fmtDate(i.shipDate), fmtDate(i.dueDate), i.totalUnits,
      i.receiptSummary?.receivedUnits || '', i.receiptSummary ? (i.receiptSummary.remainingPctLabel || `${i.receiptSummary.remainingPct}%`) : '',
      receiptNote(i) || noReceiptNote(i) || receiptUnknownNote(i) || '',
      (!i.hasRequired && i.hasReview) ? 'NEEDS RULING' : (i.hasReview ? `${i.complete ? 'COMPLETE' : 'MISSING'} + NEEDS RULING` : i.complete ? 'COMPLETE' : 'MISSING'), i.requiredCount, i.certifiedCount, i.exemptCount || 0, i.notSubjectCount || 0, i.missingCount,
      (i.missingStyles || []).join(' '), (i.pos || []).join(' '),
    ]);
    const csv = [CSV_HEADERS, ...rows].map((r) => r.map(escCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `cpsc-broker-invoices_${tenantId}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  // CHA-204: ACE-ready line-level export — one row per (commercial invoice × style) with the HTS, units, and the
  // CPSC CoC reference (version + certificate id) the broker files in ACE. The summary CSV above is per-invoice.
  // 'Cert Status Note' distinguishes a flammability exemption from a Target TTS not-subject determination;
  // both remain blank for unresolved styles.
  // CHA-228 — per-style PO / Terms / Warehouse (destination DC) / Shipment No / Container. CHA-233 — the ACE
  // header + per-style row are now built by the SHARED module (src/shared/utils/cpscBrokerCsv) so this export
  // and the daily-broker-report cron produce byte-identical CSVs (§8.2 — single source of truth, no drift).
  const exportAceCsv = () => {
    if (!visible.length) { setError('No invoices to export.'); return; }
    const rows = [];
    for (const i of visible) {
      for (const st of (i.styles || [])) rows.push(aceCellsForStyle(i, st));
    }
    const csv = [ACE_HEADERS, ...rows].map((r) => r.map(escCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `cpsc-ace-lines_${tenantId}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  if (!isAdmin) {
    return (
      <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">
        CPSC Broker Handoff is restricted to administrators.
      </div>
    );
  }

  return (
    <div className="font-plex text-carbon-ink space-y-4 max-w-5xl">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-carbon-ink">CPSC eFiling — Broker Handoff</h3>
          <p className="text-sm text-carbon-gray-70">
            One line per commercial invoice <strong>shipped to the US</strong> — <strong>complete</strong> (all
            CPSC-required styles certified) or <strong>missing</strong>. Open a row for the styles + POs. Open units
            = what's left to clear (small = mostly received). Newest ship date first.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-gray-30 text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-50">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
        </button>
      </div>

      {isPlatformRole && (
        <div className="text-xs text-carbon-blue-70 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-2">
          Acting as <strong>{role}</strong> against tenant: <strong>{tenantId}</strong>
        </div>
      )}
      {error && (
        <div className="text-sm text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-3 py-2 flex items-start gap-1">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="px-2 py-1 font-plex-mono text-[11px] uppercase border border-carbon-gray-30 bg-carbon-white text-carbon-gray-70"><strong className="text-carbon-ink">{summary.total}</strong> invoices</span>
          {excludedNonUs.length > 0 && (
            <span
              className="px-2 py-1 font-plex-mono text-[11px] uppercase bg-carbon-white text-carbon-orange-70 border border-carbon-orange-40"
              title={`Excluded by the non-US POD denylist: ${excludedNonUs.map((e) => `${e.commInv} (${(e.pods || []).join('/')})`).join(', ')}`}
            >
              <strong>{excludedNonUs.length}</strong> excluded (non-US POD)
            </span>
          )}
          {podGateDegraded && (
            <span className="px-2 py-1 font-plex-mono text-[11px] uppercase bg-carbon-white text-carbon-red-60 border border-carbon-red-60">
              POD gate degraded — nothing excluded this load
            </span>
          )}
          <span className="px-2 py-1 font-plex-mono text-[11px] uppercase bg-carbon-green-10 text-carbon-green-50 border border-carbon-green-50"><strong>{summary.complete}</strong> complete</span>
          <span className="px-2 py-1 font-plex-mono text-[11px] uppercase bg-carbon-white text-carbon-red-60 border border-carbon-red-60"><strong>{summary.missing}</strong> missing certs</span>
          {(summary.review || 0) > 0 && (
            <span
              className="px-2 py-1 font-plex-mono text-[11px] uppercase bg-carbon-white text-carbon-orange-70 border border-carbon-orange-40"
              title="Invoices with review-level styles (bags/toys/…) awaiting your adult-vs-children's ruling — expand a row to rule"
            >
              <strong>{summary.review}</strong> need ruling
            </span>
          )}
        </div>
        <div className="flex-1" />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-xs border border-carbon-gray-30 bg-carbon-white px-2 py-1.5 text-carbon-gray-80" aria-label="invoice filter">
          <option value="missing">Missing only</option>
          <option value="review">Needs ruling</option>
          <option value="complete">Complete only</option>
          <option value="all">All invoices</option>
        </select>
        <button onClick={exportCsv} disabled={!visible.length} className="flex items-center gap-1 px-2 py-1.5 text-xs border border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-gray-10 disabled:opacity-50">
          <Download size={13} /> Summary CSV
        </button>
        <button onClick={exportAceCsv} disabled={!visible.length} title="Line-level export for ACE: each style with its HTS, units, and CPSC CoC reference" className="flex items-center gap-1 px-2 py-1.5 text-xs font-semibold bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50">
          <Download size={13} /> Export for ACE (by style)
        </button>
      </div>

      {/* CHA-521 — CPSC action email: one group, or all of them. Admin-only; always sent to the configured
          operator address so the content is reviewed before it reaches production staff. */}
      {isAdmin && actionGroups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 bg-carbon-gray-10 border border-carbon-border">
          <span className="text-xs text-carbon-gray-70">
            CPSC action email — <strong>{actionGroups.length}</strong> group{actionGroups.length === 1 ? '' : 's'} with missing styles
          </span>
          <div className="flex-1" />
          <select
            value={actionGroup}
            onChange={(e) => setActionGroup(e.target.value)}
            disabled={actionBusy}
            aria-label="division and customer group"
            className="text-xs border border-carbon-gray-30 bg-carbon-white px-2 py-1.5 text-carbon-gray-80 disabled:opacity-50"
          >
            <option value="">Select a division / customer…</option>
            {actionGroups.map((g) => (
              <option key={g.key} value={g.key}>
                Div {g.division || '—'} · {g.customer || '—'} ({g.styles} missing)
              </option>
            ))}
          </select>
          <button
            onClick={() => sendActionEmail(actionGroups.find((g) => g.key === actionGroup), { all: false })}
            disabled={actionBusy || !actionGroup}
            title="Email this division / customer group, with the fill-in workbook attached"
            className="flex items-center gap-1 px-2 py-1.5 text-xs border border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-gray-10 disabled:opacity-50"
          >
            {actionBusy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email this group
          </button>
          <button
            onClick={() => sendActionEmail(null, { all: true })}
            disabled={actionBusy}
            title="Email every division / customer group that has missing styles"
            className="flex items-center gap-1 px-2 py-1.5 text-xs font-semibold bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50"
          >
            {actionBusy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email all groups to me
          </button>
          {actionMsg && (
            <span className={`w-full text-xs ${actionMsg.ok ? 'text-carbon-green-60' : 'text-carbon-red-60'}`}>{actionMsg.text}</span>
          )}
        </div>
      )}

      <div className="bg-carbon-white border border-carbon-border overflow-x-auto">
        <CpscTable
          className="w-full text-xs"
          headClassName={`${CPSC_TH_CLASS} text-left`}
          columns={[
            { label: '', className: 'px-2 py-2 w-6' },
            { label: 'Commercial Invoice', className: 'px-3 py-2 font-medium' },
            { label: 'Div', className: 'px-3 py-2 font-medium' },
            { label: 'Customer', className: 'px-3 py-2 font-medium' },
            { label: 'Ship Date', className: 'px-3 py-2 font-medium' },
            { label: 'Due Date', className: 'px-3 py-2 font-medium' },
            { label: 'Open Units', className: 'px-3 py-2 font-medium text-right' },
            { label: 'Status', className: 'px-3 py-2 font-medium' },
          ]}
        >
            {loading && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-carbon-gray-70"><span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading shipped invoices…</span></td></tr>
            )}
            {!loading && !visible.length && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-carbon-gray-50">
                {filter === 'missing' ? 'No invoices missing certs — all shipped invoices are complete. 🎉' : 'No invoices to show.'}
              </td></tr>
            )}
            {!loading && visible.map((i) => {
              const open = expanded.has(i.commInv);
              return (
                <React.Fragment key={i.commInv}>
                  <tr
                    onClick={() => toggle(i.commInv)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle(i.commInv);
                      }
                    }}
                    className="border-t border-carbon-gray-10 hover:bg-carbon-gray-10 cursor-pointer"
                  >
                    <td className="px-2 py-2 text-carbon-gray-40">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                    <td className="px-3 py-2 font-plex-mono font-medium text-carbon-ink whitespace-nowrap">{i.commInv}</td>
                    <td className="px-3 py-2 text-carbon-gray-70 whitespace-nowrap">{i.division || '—'}</td>
                    <td className="px-3 py-2 text-carbon-gray-70 whitespace-nowrap">{i.customer || '—'}</td>
                    <td className="px-3 py-2 font-plex-mono text-carbon-gray-70 whitespace-nowrap">{fmtDate(i.shipDate)}</td>
                    <td className="px-3 py-2 font-plex-mono text-carbon-gray-70 whitespace-nowrap">{fmtDate(i.dueDate)}</td>
                    <td className="px-3 py-2 font-plex-mono text-carbon-gray-80 text-right whitespace-nowrap">{fmtUnits(i.totalUnits)}</td>
                    <td className="px-3 py-2">
                      {/* CHA-281 — review-only invoices are neither complete nor missing: they await the ruling. */}
                      {!i.hasRequired && i.hasReview
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 font-plex-mono text-[10px] bg-carbon-white text-carbon-orange-70 border border-carbon-orange-40 whitespace-nowrap"><AlertTriangle size={12} /> NEEDS RULING {i.reviewCount}</span>
                        : i.complete
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 font-plex-mono text-[10px] bg-carbon-green-10 text-carbon-green-50 border border-carbon-green-50 whitespace-nowrap"><CheckCircle2 size={12} /> COMPLETE</span>
                          : <span className="inline-flex items-center gap-1 px-2 py-0.5 font-plex-mono text-[10px] bg-carbon-white text-carbon-red-60 border border-carbon-red-60 whitespace-nowrap"><XCircle size={12} /> MISSING {i.missingCount}/{i.requiredCount}</span>}
                      {i.hasRequired && i.hasReview ? (
                        <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 font-plex-mono text-[9.5px] bg-carbon-white text-carbon-orange-70 border border-carbon-orange-40 whitespace-nowrap" title="This invoice also carries review-level styles awaiting the adult/children's ruling">
                          +{i.reviewCount} ruling
                        </span>
                      ) : null}
                      {receiptNote(i) ? (
                        <span className="mt-1 inline-flex px-1.5 py-0.5 font-plex-mono text-[9.5px] bg-carbon-blue-10 text-carbon-blue-70 border border-carbon-blue-60 whitespace-nowrap" title="CPSC is complete/missing independently; this note comes from received vs shipped open-WIP balance.">
                          {i.receiptSummary.remainingPctLabel || `${i.receiptSummary.remainingPct}%`} left
                        </span>
                      ) : null}
                      {noReceiptNote(i) ? (
                        <span className="mt-1 inline-flex px-1.5 py-0.5 font-plex-mono text-[9.5px] bg-carbon-orange-10 text-carbon-orange-70 border border-carbon-orange-40 whitespace-nowrap" title="CPSC is complete; this invoice is still open in WIP and has no receipt row in the covered receipts feed.">
                          no receipt
                        </span>
                      ) : null}
                      {receiptUnknownNote(i) ? (
                        <span className="mt-1 inline-flex px-1.5 py-0.5 font-plex-mono text-[9.5px] bg-carbon-white text-carbon-gray-70 border border-carbon-gray-30 whitespace-nowrap" title={receiptUnknownNote(i)}>
                          receipt unknown
                        </span>
                      ) : null}
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-carbon-gray-10/60">
                      <td />
                      <td colSpan={7} className="px-3 pb-3 pt-1">
                        <div className="text-[11px] text-carbon-gray-70 mb-1.5">POs: <span className="font-plex-mono">{(i.pos || []).join(', ') || '—'}</span></div>
                        <div className="text-[11px] text-carbon-gray-70 mb-1.5">
                          Ship date: <span className="font-plex-mono">{fmtDate(i.shipDate)}</span>
                          <span className="mx-1.5 text-carbon-gray-40">·</span>
                          Due date: <span className="font-plex-mono">{fmtDate(i.dueDate)}</span>
                          {receiptNote(i) ? (
                            <>
                              <span className="mx-1.5 text-carbon-gray-40">·</span>
                              <span className="text-carbon-blue-70">{receiptNote(i)}; remaining balance is operational open-WIP clearance, not a CPSC hold.</span>
                            </>
                          ) : null}
                          {noReceiptNote(i) ? (
                            <>
                              <span className="mx-1.5 text-carbon-gray-40">·</span>
                              <span className="text-carbon-orange-70">{noReceiptNote(i)} This is an operational receiving signal, not a CPSC hold.</span>
                            </>
                          ) : null}
                          {receiptUnknownNote(i) ? (
                            <>
                              <span className="mx-1.5 text-carbon-gray-40">·</span>
                              <span className="text-carbon-gray-70">{receiptUnknownNote(i)}</span>
                            </>
                          ) : null}
                        </div>
                        <table className="w-full text-[11px]">
                          <thead className="font-plex-mono text-[9.5px] uppercase tracking-[0.1em] text-carbon-gray-50 text-left">
                            {/* CHA-228 — PO / Terms / Warehouse (DC) / Shipment / Container surfaced per style alongside HTS/Units/cert. */}
                            <tr><th className="py-1 font-medium">Style</th><th className="py-1 font-medium">PO</th><th className="py-1 font-medium">Terms</th><th className="py-1 font-medium">Warehouse</th><th className="py-1 font-medium">Shipment</th><th className="py-1 font-medium">Container</th><th className="py-1 font-medium">HTS</th><th className="py-1 font-medium text-right">Units</th><th className="py-1 font-medium">CPSC</th><th className="py-1 font-medium">Certificate</th></tr>
                          </thead>
                          <tbody>
                            {i.styles.map((st) => (
                              <tr key={st.sty} className="border-t border-carbon-gray-10">
                                <td className="py-1 font-plex-mono text-carbon-gray-80">{st.sty}</td>
                                <td className="py-1 font-plex-mono text-carbon-gray-50">{st.po || '—'}</td>
                                <td className="py-1 text-carbon-gray-50">{st.terms || '—'}</td>
                                <td className="py-1 text-carbon-gray-50">{st.warehouse || '—'}</td>
                                <td className="py-1 font-plex-mono text-carbon-gray-50">{st.shipmentNum || '—'}</td>
                                <td className="py-1 font-plex-mono text-carbon-gray-50">{st.container || '—'}</td>
                                <td className="py-1 text-carbon-gray-50">{st.hts || '—'}</td>
                                <td className="py-1 font-plex-mono text-carbon-gray-50 text-right">{fmtUnits(st.units)}</td>
                                <td className="py-1 text-carbon-gray-50">
                                  {st.notSubject ? 'not subject'
                                    : st.requiresCpsc
                                    ? (st.classification === 'childrens' ? "required (children's)" : 'required')
                                    : st.reviewPending ? <span className="text-carbon-orange-70">needs ruling</span>
                                      : st.classification === 'adult' ? 'adult — n/a'
                                        : '—'}
                                </td>
                                <td className="py-1">
                                  {/* CHA-281 — an unruled review-level style gets the inline adult/children's chooser.
                                      The click is the ruling of record (audited server-side). */}
                                  {st.reviewPending ? (
                                    <span className="inline-flex items-center gap-1.5">
                                      <button
                                        type="button"
                                        disabled={rulingBusy !== null}
                                        onClick={() => rule(st.sty, 'adult', st.hts)}
                                        className="px-2 py-0.5 font-plex-mono text-[10px] border border-carbon-gray-30 text-carbon-gray-80 bg-carbon-white hover:bg-carbon-gray-10 disabled:opacity-50"
                                        title="Rule this style ADULT — no CPSC cert needed (recorded + auditable)"
                                      >
                                        Adult
                                      </button>
                                      <button
                                        type="button"
                                        disabled={rulingBusy !== null}
                                        onClick={() => rule(st.sty, 'childrens', st.hts)}
                                        className="px-2 py-0.5 font-plex-mono text-[10px] border border-carbon-orange-40 text-carbon-orange-70 bg-carbon-white hover:bg-carbon-gray-10 disabled:opacity-50"
                                        title="Rule this style CHILDREN'S — a CPC becomes required and this invoice moves to the broker worklist"
                                      >
                                        Children&rsquo;s
                                      </button>
                                      {rulingBusy === st.sty ? <span className="text-carbon-gray-40">saving…</span> : null}
                                    </span>
                                  ) : !st.requiresCpsc ? <span className="text-carbon-gray-40">n/a</span>
                                    : st.certified ? <span className="inline-flex items-center gap-1 text-carbon-green-50"><CheckCircle2 size={11} /> certified</span>
                                      /* CHA-222 PR3 — a flammability-exempt required style is satisfied (no cert to request); teal, with the broker note. */
                                      : st.exempt ? <span className="inline-flex items-center gap-1 text-carbon-teal-60" title={st.certStatusNote || ''}><CheckCircle2 size={11} /> exempt</span>
                                        : st.notSubject ? <span className="inline-flex items-center gap-1 text-carbon-gray-70" title={st.certStatusNote || ''}><CheckCircle2 size={11} /> not subject</span>
                                        : <span className="inline-flex items-center gap-1 text-carbon-red-60"><XCircle size={11} /> {st.certStatus ? st.certStatus.replace(/_/g, ' ') : 'no cert'}</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </CpscTable>
      </div>

      <p className="text-[11px] text-carbon-gray-50 flex items-center gap-1">
        <FileSpreadsheet size={11} /> Source: shipped open-WIP (commercial invoice → style) + CPSC certificates. Open units = the open WIP balance still to clear. A style counts only if its HTS requires CPSC (apparel).
      </p>
    </div>
  );
}
